import { useContext, useEffect, useState, useCallback, useRef } from "react";
import LineChartJS from "../charts/LineChartJs";
import BarChartStacked from "../charts/StackeBarChart";
import BarChart from "../charts/BarChart";
import MultipleAxis from "../charts/MultipleAxisChart";
import PyramidChart from "../charts/Pyramid";
import PieChart from "../charts/PieChart";
import DonutChart from "../charts/DonutChart";
import StatusChart from "../charts/StatusChart";

import { Box, Fab } from "@mui/material";
import { useParams } from "react-router-dom";
import Sidebar from "../../global/Sidebar";
import { FiltersContext } from "../../store/filters-options-context";
import LazyLoadItem from "./SingleIndicator";
import HeaderFixed from "./FixedHeader";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ScrollTop } from "../../utils/global";
import { useGroupedItems } from "../../hooks/useGroupedItems";
import Footer from "../Footer";
import LoadProgress from "../../global/Loading";


export default function Detail({ perChartLoading = true, ...props }) {
    const { scroll: scrollParam } = useParams();
    const {
        params,
        setParams,
        itemsList,
        subCategory,
        valueSelected,
        yearRange,
        countryCode,
        regions,
        signalDataLoadingComplete
    } = useContext(FiltersContext);
    // Country label can be derived from current selection
    const country = valueSelected?.area?.label;
    
    const groupedItems = useGroupedItems(itemsList, params, subCategory);

    // Global loading overlay: count in-flight chart fetches from visible items
    const [loadingCount, setLoadingCount] = useState(0);
    
    // Track initial anchor from URL for positioning
    const [initialAnchor, setInitialAnchor] = useState(null);

    const handleLoadingStart = useCallback(() => {
        setLoadingCount((c) => c + 1);
    }, []);

    const handleLoadingEnd = useCallback(() => {
        setLoadingCount((c) => {
            const newCount = c > 0 ? c - 1 : 0;
            // When all loading is complete, signal the slider to disable temporarily
            if (newCount === 0) {
                signalDataLoadingComplete();
            }
            return newCount;
        });
    }, [signalDataLoadingComplete]);

    // Scroll-based URL updates are handled by Sidebar's activeMenu effect

    // When the chart set changes (e.g., Select a Theme), reset loading counters
    useEffect(() => {
        setLoadingCount(0);
        // We specifically watch itemsList and subCategory (chart configuration drivers)
    }, [itemsList, subCategory]);

    // Ref to track if we've already handled the initial URL-based anchor scroll
    const hasHydratedRef = useRef(false);

    // Capture initial anchor synchronously at render time, before scroll-tracking effects can overwrite the URL
    const capturedInitialAnchor = useRef(scrollParam || null);

    // Extract and store initial anchor from query (?scroll=...) on component mount
    useEffect(() => {
        if (Object.keys(groupedItems).length > 0 && !hasHydratedRef.current) {
            const anchor = capturedInitialAnchor.current;
            if (anchor && anchor !== '0') {
                hasHydratedRef.current = true;
                setInitialAnchor(anchor);
                if ('scrollRestoration' in window.history) {
                    window.history.scrollRestoration = 'manual';
                }
            }
        }
    }, [groupedItems]);

    
    // Handle scrolling to anchor after charts are loaded
    useEffect(() => {
        if (!initialAnchor || loadingCount > 0) return;
        
        const scrollToAnchor = () => {
            const targetElement = document.getElementById(initialAnchor);
            const container = document.getElementById("scroll-content");
            
            if (targetElement && container) {
                const elementOffsetTop = targetElement.offsetTop;
                const scrollOffset = Math.max(0, elementOffsetTop - 50);
                
                // Set position immediately without animation
                container.scrollTop = scrollOffset;
                
                // Clear the anchor after successful scroll
                setInitialAnchor(null);
            } else {
                // If element doesn't exist yet, try again after a short delay
                setTimeout(scrollToAnchor, 100);
            }
        };
        
        // Small delay to ensure DOM is ready
        setTimeout(scrollToAnchor, 50);
        
        return () => {
            // Restore scroll restoration for other pages
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, [initialAnchor, loadingCount]);

    return (
        <Box display="flex" flexDirection="row" className="container">
            <Sidebar />
            <Box
                display="flex"
                flexDirection="column"
                className="right-column"
            >
                {/* Legacy mode: one overlay covering the page while any chart is fetching */}
                {!perChartLoading && <LoadProgress isFetching={loadingCount > 0} />}
                <HeaderFixed
                    country={valueSelected.area}
                    params={params}
                    setParams={setParams}
                    yearRange={yearRange}
                />
                <Box id="scroll-content" sx={{ backgroundColor: "secondary.main" }}>
                    {(() => {
                        // Build a stable render-order map using the canonical item id (fallback to order index)
                        const categoryKeys = Object.keys(groupedItems);
                        const idOrderMap = new Map();
                        let orderCounter = 0;
                        categoryKeys.forEach((cat) => {
                            groupedItems[cat].forEach((gi, localIdx) => {
                                const fallbackId = `${cat}-${localIdx}`;
                                const canonicalId = (gi?.info && (gi.info.id ?? gi.info.item_id)) ?? fallbackId;
                                const key = String(canonicalId);
                                if (!idOrderMap.has(key)) {
                                    idOrderMap.set(key, orderCounter);
                                }
                                orderCounter++;
                            });
                        });

                        const targetOrder = initialAnchor != null ? idOrderMap.get(String(initialAnchor)) : null;

                        return Object.keys(groupedItems).map((category) => (
                            <div key={category} id={category} style={{ marginBottom: '100px' }}>
                                {groupedItems[category].map((item, index) => {
                                    // Prefer the data item id from info; fallback to a stable composite id to avoid collisions
                                    const fallbackId = `${category}-${index}`;
                                    const canonicalId = (item?.info && (item.info.id ?? item.info.item_id)) ?? fallbackId;
                                    const renderOrder = idOrderMap.get(String(canonicalId)) ?? null;
                                const ChartComponent =
                                    item.info.chart_type === "line"
                                        ? LineChartJS
                                        : item.info.chart_type === "barChart"
                                            ? BarChart
                                            : item.info.chart_type === "pyramid"
                                                ? PyramidChart
                                                : item.info.chart_type === "multipleAxis"
                                                    ? MultipleAxis
                                                    : item.info.chart_type === "pie"
                                                        ? PieChart
                                                        : item.info.chart_type === "donut"
                                                            ? DonutChart
                                                            : item.info.chart_type === "status"
                                                                ? StatusChart
                                                                : BarChartStacked;
                                    return (
                                        <LazyLoadItem
                                            key={canonicalId}
                                            url={item.query}
                                            info={item.info}
                                            ChartComponent={ChartComponent}
                                            title={item.title}
                                            itemId={canonicalId}
                                            renderOrder={renderOrder}
                                            targetOrder={targetOrder}
                                            item_code={item.info.item_code}
                                            country={country}
                                            countryCode={countryCode}
                                            onLoadingStart={handleLoadingStart}
                                            onLoadingEnd={handleLoadingEnd}
                                            initialAnchor={initialAnchor}
                                            perChartLoading={perChartLoading}
                                        dataSource={item.info.domain_code
                                            .split(".")
                                            .pop()
                                            .replaceAll("mvw_", "")
                                            .replaceAll("vw_", "")
                                            .replaceAll("data_", "")
                                            .toUpperCase()}
                                        chartTitle={item.info.label}
                                        regions={regions}
                                        isPoU={item.info.isPoU}
                                        isFoodInsecurity={item.info.isFoodInsecurity}
                                        domain_code={item.info.domain_code}
                                    />
                                );
                                })}
                            </div>
                        ));
                    })()}
                    <ScrollTop {...props}>
                        <Fab sx={(theme) => ({
                            transform: {
                                xs: 'scale(0.8)',
                                md: 'none',
                            },
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                        })}
                            size="small" aria-label="scroll back to top">
                            <KeyboardArrowUpIcon />
                        </Fab>
                    </ScrollTop>
                    <Footer />
                </Box>
            </Box>
        </Box>
    );
}
